import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import Header from "../components/Header";
import { ArrowLeft, CheckCircle } from "@phosphor-icons/react";

export default function ProjectNew() {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    name: "",
    product_description: "",
    problem_criteria: "",
    suggested_response: "",
    keywords: "",
    schedule_hours: 6,
    confidence_threshold: 0.6,
    per_source_limit: 15,
    enabled: true,
  });

  const set = (k, v) => setForm({ ...form, [k]: v });

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const payload = {
        ...form,
        keywords: form.keywords
          .split(/[\n,]/)
          .map((k) => k.trim())
          .filter(Boolean),
        schedule_hours: Number(form.schedule_hours) || 6,
        confidence_threshold: Number(form.confidence_threshold) || 0.6,
        per_source_limit: Number(form.per_source_limit) || 15,
      };
      const { data } = await api.post("/projects", payload);
      navigate(`/projects/${data.id}`);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Header />
      <main className="px-6 md:px-10 py-8 max-w-3xl mx-auto" data-testid="project-new-page">
        <button onClick={() => navigate(-1)} className="btn-ghost mb-6 flex items-center gap-2" data-testid="back-btn">
          <ArrowLeft size={14} /> back
        </button>

        <div className="mb-8">
          <div className="text-[10px] font-mono text-[#ffb000] uppercase tracking-[0.3em] mb-2">
            [NEW PROJECT]
          </div>
          <h1 className="text-4xl font-bold tracking-tighter">Configure your hunt</h1>
          <p className="text-[#888] text-sm mt-2 max-w-xl">
            Define the product, the problem to look for, and the response approach. The engine
            uses these to score every found post for relevance — never spam.
          </p>
        </div>

        {err && (
          <div className="mb-4 p-3 border border-[#ff3b30]/40 bg-[#ff3b30]/10 text-[#ff3b30] text-xs font-mono" data-testid="form-error">
            {err}
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-6" data-testid="project-form">
          <div className="panel p-6">
            <div className="grid grid-cols-1 gap-5">
              <div>
                <label className="field-label">Project name *</label>
                <input
                  data-testid="project-name-input"
                  required
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder="Memory Map"
                  className="field-input"
                />
              </div>
              <div>
                <label className="field-label">Product description</label>
                <textarea
                  data-testid="product-description-input"
                  value={form.product_description}
                  onChange={(e) => set("product_description", e.target.value)}
                  rows={3}
                  placeholder="What does your product/site do? Used by the AI to judge fit."
                  className="field-input resize-none"
                />
              </div>
              <div>
                <label className="field-label">Problem criteria *</label>
                <textarea
                  data-testid="problem-criteria-input"
                  required
                  value={form.problem_criteria}
                  onChange={(e) => set("problem_criteria", e.target.value)}
                  rows={3}
                  placeholder="e.g. user is struggling to remember where they put items at home"
                  className="field-input resize-none"
                />
              </div>
              <div>
                <label className="field-label">Suggested helpful response (template)</label>
                <textarea
                  data-testid="suggested-response-input"
                  value={form.suggested_response}
                  onChange={(e) => set("suggested_response", e.target.value)}
                  rows={3}
                  placeholder="A short, non-spammy reply you'd offer — for your own reference when posting."
                  className="field-input resize-none"
                />
              </div>
              <div>
                <label className="field-label">Keywords (comma or newline separated)</label>
                <textarea
                  data-testid="keywords-input"
                  value={form.keywords}
                  onChange={(e) => set("keywords", e.target.value)}
                  rows={3}
                  placeholder="forgetting where I put, can't remember, lost my keys"
                  className="field-input resize-none"
                />
                <div className="mt-1 text-[10px] font-mono text-[#555] uppercase tracking-widest">
                  used for cheap pre-filter before LLM scoring
                </div>
              </div>
            </div>
          </div>

          <div className="panel p-6">
            <div className="label-tag mb-4">Engine config</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <label className="field-label">Schedule (hours)</label>
                <input
                  data-testid="schedule-hours-input"
                  type="number"
                  min={1}
                  value={form.schedule_hours}
                  onChange={(e) => set("schedule_hours", e.target.value)}
                  className="field-input"
                />
              </div>
              <div>
                <label className="field-label">Confidence threshold</label>
                <input
                  data-testid="confidence-threshold-input"
                  type="number"
                  min={0}
                  max={1}
                  step={0.05}
                  value={form.confidence_threshold}
                  onChange={(e) => set("confidence_threshold", e.target.value)}
                  className="field-input"
                />
              </div>
              <div>
                <label className="field-label">Per-source limit</label>
                <input
                  data-testid="per-source-limit-input"
                  type="number"
                  min={1}
                  max={50}
                  value={form.per_source_limit}
                  onChange={(e) => set("per_source_limit", e.target.value)}
                  className="field-input"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between gap-4 pt-2">
            <p className="text-[10px] font-mono text-[#555] uppercase tracking-[0.2em] flex items-center gap-2">
              <CheckCircle size={12} color="#00ff66" /> 25+ default free sources will be added automatically
            </p>
            <button type="submit" disabled={busy} className="btn-primary" data-testid="create-project-submit">
              {busy ? "CREATING…" : "CREATE PROJECT"}
            </button>
          </div>
        </form>
      </main>
    </div>
  );
}
